import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import matterportService from '../services/matterportService';
import audioService from '../services/audioService';
import { assetUrl } from '../utils/assetUrl';
import { useLanguage } from '../context/LanguageContext';
import { PROJECT_CONFIG } from '../config/projectConfig';
import { Z_INDEX } from '../const';
import quickNavConfig from '../config/quickNavConfig.json';
import CaptionsButton from './Captions/CaptionsButton';
import './LocationQuickNav.css';

// Play the per-stop standalone narration for a footer quick-nav click.
// File pattern: /assets/audio/narration/<narrationKey>_standalone_<lang>.mp3
const playStandaloneNarration = (location, lang) => {
  const key = location?.narrationKey;
  if (!key) return false;
  const src = assetUrl(`/assets/audio/narration/${key}_standalone_${lang}.mp3`);
  audioService.duckForTour();
  audioService.playTourNarration(
    src,
    () => { audioService.unduckForTour(); },
    () => { audioService.unduckForTour(); },
    key,
  );
  return true;
};

const TOUR_LABEL = {
  mr: { label: 'मार्गदर्शित दर्शन', icon: '▶' },
  en: { label: 'Guided Tour', icon: '▶' },
  hi: { label: 'मार्गदर्शित दर्शन', icon: '▶' },
};

/**
 * Bottom dock: 12 fixed key stops for the Mandir digital twin.
 * - Buttons 1–11 fly the camera via the tour snapshot SID.
 * - Button 12 (Sky View) opens the drone aerial view.
 * - During a guided tour the button for the current stop is highlighted;
 *   when the tour reaches the Sky View stop the aerial view opens automatically.
 */
const LocationQuickNav = ({ onSkyView }) => {
  const { language, setLanguage } = useLanguage();
  const [navigating, setNavigating] = useState(null);
  // Active guided-tour stop tracked by location `id` rather than `tourSid`,
  // because multiple stops can share the same Matterport snapshot (e.g.
  // Mahaveer Bandhara reuses Mandir Entrance's sweep). The tour step `index`
  // is the source of truth — we resolve it to a location id here.
  const [activeLocationId, setActiveLocationId] = useState(null);
  const [tourPaused, setTourPaused] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  // Hidden curator tool: Alt+Shift+E toggles "Edit View" mode, which lets the
  // operator capture the current sweep / rotation / zoom for any 3D footer
  // stop and copy the JSON block into quickNavConfig.json. Image-only and
  // Sky View stops are skipped (they don't have a 3D camera to author).
  const [editMode, setEditMode] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  // Forces a re-render of the editor panel so the live pose readout updates
  // even though it's read from a singleton service (no React state changes).
  const [, setEditTick] = useState(0);
  const locationsRef = useRef(null);

  // Keep onSkyView fresh without re-subscribing to SDK events
  const onSkyViewRef = useRef(onSkyView);
  useEffect(() => { onSkyViewRef.current = onSkyView; }, [onSkyView]);

  // Track current language in a ref so the tour-step listener can build
  // localized captions without needing to re-subscribe to SDK events.
  const languageRef = useRef(language);
  useEffect(() => { languageRef.current = language; }, [language]);

  // Re-entry guard for handleNavigate — see handler comment.
  const navigatingRef = useRef(null);

  // Listen for tour step / pause / resume events → update active + paused state.
  // When the tour reaches the Sky View stop, open the aerial view without
  // stopping the tour — narration keeps playing and the tour completes
  // naturally. (handleSkyView from MatterportViewer is only used for the
  // manual footer click which intentionally takes over from the tour.)
  useEffect(() => {
    const onTourStep = ({ sid, index }) => {
      // Prefer the tour-step `index` to disambiguate stops that share a
      // tourSid; fall back to first sid match for safety.
      const tourLocs = quickNavConfig.locations.filter(
        (l) => !l.excludeFromTour
      );
      const match =
        (typeof index === 'number' && tourLocs[index]) ||
        quickNavConfig.locations.find((l) => l.tourSid === sid);
      setActiveLocationId(match?.id || null);
      setTourPaused(false);
      if (match?.action === 'skyView') {
        window.dispatchEvent(new CustomEvent('open360View'));
      } else {
        // Any non-skyView step: ensure the drone overlay is dismissed so
        // the user is back inside the Matterport scene.
        window.dispatchEvent(new CustomEvent('close360View'));
      }
    };
    const onTourStop = () => {
      setActiveLocationId(null);
      setTourPaused(false);
    };
    const onTourPause = () => setTourPaused(true);
    const onTourResume = () => setTourPaused(false);
    matterportService.addEventListener('tourStep', onTourStep);
    matterportService.addEventListener('tourStop', onTourStop);
    matterportService.addEventListener('tourPause', onTourPause);
    matterportService.addEventListener('tourResume', onTourResume);
    return () => {
      matterportService.removeEventListener('tourStep', onTourStep);
      matterportService.removeEventListener('tourStop', onTourStop);
      matterportService.removeEventListener('tourPause', onTourPause);
      matterportService.removeEventListener('tourResume', onTourResume);
    };
  }, []);

  // Auto-scroll the active button into view inside the scrollable strip
  useEffect(() => {
    if (!activeLocationId || !locationsRef.current) return;
    const el = locationsRef.current.querySelector(`[data-location-id="${activeLocationId}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }, [activeLocationId]);

  // Toggle body class while a guided tour is running. CSS uses this to hide
  // the footer dock, side gallery, media tray and most header chrome — only
  // the temple title + logo and the floating tour-control panel remain.
  useEffect(() => {
    const running = activeLocationId !== null;
    document.body.classList.toggle('tour-running', running);
    return () => { document.body.classList.remove('tour-running'); };
  }, [activeLocationId]);

  // Tell the SDK service which stops the guided tour should walk through,
  // in the order they appear in the footer dock. This filters out any extra
  // SDK tour snapshots so Next/Prev never flashes through unmapped points.
  // Synthetic SIDs (e.g. the Nursery image-only stop) become placeholder
  // steps that just emit `tourStep` without moving the camera.
  useEffect(() => {
    // Guided tour walks every stop except those flagged excludeFromTour
    // (e.g. Sky View — still clickable in the footer, just not auto-played).
    const sids = quickNavConfig.locations
      .filter((l) => !l.excludeFromTour)
      .map((l) => l.tourSid)
      .filter(Boolean);
    matterportService.setTourOrder?.(sids);

    // Register per-location camera overrides so both manual quick-nav and
    // guided-tour steps use app-authored camera configs when present.
    // Stops without a `camera` block fall through to legacy behaviour.
    const overrides = {};
    for (const loc of quickNavConfig.locations) {
      if (loc.tourSid && loc.camera) overrides[loc.tourSid] = loc.camera;
    }
    matterportService.setLocationCameraOverrides?.(overrides);
  }, []);

  // Track whether the strip can scroll further left / right so we can
  // show/hide the arrow affordances accordingly. The inner
  // `.location-quick-nav__locations` strip is the scroller on every
  // breakpoint; the outer bar no longer scrolls on mobile.
  const getScroller = useCallback(() => {
    return locationsRef.current || null;
  }, []);

  useEffect(() => {
    let scroller = getScroller();
    if (!scroller) return;

    const update = () => {
      const el = getScroller();
      if (!el) return;
      const { scrollLeft, scrollWidth, clientWidth } = el;
      // 2px tolerance for subpixel rounding
      setCanScrollLeft(scrollLeft > 2);
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 2);
    };

    const attach = () => {
      scroller = getScroller();
      if (!scroller) return;
      scroller.addEventListener('scroll', update, { passive: true });
    };
    const detach = () => {
      if (scroller) scroller.removeEventListener('scroll', update);
    };

    attach();
    update();
    const onResize = () => { detach(); attach(); update(); };
    window.addEventListener('resize', onResize);

    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(update);
      if (locationsRef.current) ro.observe(locationsRef.current);
      if (scroller && scroller !== locationsRef.current) ro.observe(scroller);
    }

    return () => {
      detach();
      window.removeEventListener('resize', onResize);
      if (ro) ro.disconnect();
    };
  }, [getScroller]);

  const scrollStrip = useCallback((direction) => {
    const el = getScroller();
    if (!el) return;
    // Scroll by ~70% of visible width for a natural "paginate" feel
    const amount = Math.max(160, Math.round(el.clientWidth * 0.7));
    el.scrollBy({ left: direction * amount, behavior: 'smooth' });
  }, [getScroller]);

  const handleNavigate = useCallback(async (location) => {
    // Re-entry guard: if a previous click is still flying / opening
    // overlays, ignore the next one. The button's `disabled` prop
    // covers the 3D-nav path, but the skyView path used to bypass it
    // and could double-fire narration on a fast double-tap.
    if (navigatingRef.current) return;
    navigatingRef.current = location.id;
    try {
      // Sky View stop: open the aerial drone view instead of navigating
      if (location.action === 'skyView') {
        await matterportService.stopTour({ skipReturn: true });
        if (typeof onSkyViewRef.current === 'function') onSkyViewRef.current();
        try { audioService.unlockOnGesture?.(); } catch (_) {}
        try { if (audioService.isMuted?.()) audioService.setMuted(false); } catch (_) {}
        playStandaloneNarration(location, language);
        return;
      }

      if (!location.tourSid) {
        console.warn(`⚠️ tourSid not configured for ${location.id}`);
        return;
      }

      // Stop any running guided tour — user is taking manual control
      await matterportService.stopTour({ skipReturn: true });
      window.dispatchEvent(new CustomEvent('close360View'));

      try {
        setNavigating(location.id);
        await matterportService.navigateToTourSnapshotBySid(location.tourSid, location.camera || null);
        // Tell the per-stop gallery which location is now active
        window.dispatchEvent(new CustomEvent('activeLocationChanged', {
          detail: { tourSid: location.tourSid, id: location.id }
        }));
        // Play the per-stop standalone narration. Footer click is a direct
        // user gesture so the AudioContext is allowed to start here.
        try { audioService.unlockOnGesture?.(); } catch (_) {}
        try { if (audioService.isMuted?.()) audioService.setMuted(false); } catch (_) {}
        const playedStandalone = playStandaloneNarration(location, language);
        // ONLY fall back to the proximity POI auto-trigger when this stop
        // has no per-stop standalone narration. Otherwise the standalone
        // clip (audioService) and the POI tag voiceover (voiceoverService)
        // both start within ~300 ms and overlap — sounding like the first
        // line replays. Standalone is the canonical narration for the stop.
        if (!playedStandalone) {
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('quickNavArrival', {
              detail: { tourSid: location.tourSid, id: location.id }
            }));
          }, 300);
        }
      } catch (err) {
        console.error(`❌ Failed to navigate to ${location.id}:`, err);
      } finally {
        setNavigating(null);
      }
    } finally {
      navigatingRef.current = null;
    }
  }, [language]);

  const handleStartTour = useCallback(async () => {
    // Unlock Web Audio inside this user-gesture call stack so iOS/Android browsers
    // allow the tour narration to autoplay on subsequent step changes.
    try { audioService.unlockOnGesture?.(); } catch (_) {}
    // Prime the narration HTMLAudioElement with a synchronous play()/
    // pause() inside this gesture stack. Without this, iOS Safari
    // rejects the first narration (which plays seconds later, after
    // the camera fly) with NotAllowedError, and the tour starts silent.
    try { audioService.primeTourNarration?.(); } catch (_) {}
    // If the user previously muted via the header, an explicit "Start
    // Guided Tour" tap is treated as intent to hear narration again.
    try { if (audioService.isMuted?.()) audioService.setMuted(false); } catch (_) {}
    window.dispatchEvent(new CustomEvent('close360View'));
    try {
      setNavigating('guided_tour');
      await matterportService.startTour();
    } catch (err) {
      console.error('❌ Failed to start guided tour:', err);
    } finally {
      setNavigating(null);
    }
  }, []);

  const handleTourPrev = useCallback(() => { matterportService.tourPrev(); }, []);
  const handleTourNext = useCallback(() => { matterportService.tourNext(); }, []);
  const handleTourStop = useCallback(() => { matterportService.stopTour(); }, []);
  const handleTourTogglePause = useCallback(() => {
    if (matterportService.isTourPaused?.()) matterportService.resumeTour();
    else matterportService.pauseTour();
  }, []);

  // ── Curator camera-edit mode ─────────────────────────────────────────────
  // Toggle with Alt+Shift+E. When on, each 3D footer button gets a small
  // pencil affordance; clicking it navigates to that stop and opens an
  // editor panel that shows the live sweep/rotation/zoom and lets the
  // curator copy the resulting `camera` block into quickNavConfig.json.
  useEffect(() => {
    const onKey = (e) => {
      if (e.altKey && e.shiftKey && (e.key === 'E' || e.key === 'e')) {
        e.preventDefault();
        setEditMode((v) => {
          const next = !v;
          if (!next) setEditingLocation(null);
          // eslint-disable-next-line no-console
          console.log(next ? '✏️ QuickNav edit mode ON' : '✏️ QuickNav edit mode OFF');
          return next;
        });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Poll the SDK pose at 4 Hz while the editor panel is open so the readout
  // tracks the user's manual camera adjustments without subscribing here.
  useEffect(() => {
    if (!editingLocation) return undefined;
    const id = setInterval(() => setEditTick((t) => t + 1), 250);
    return () => clearInterval(id);
  }, [editingLocation]);

  const handleEditLocation = useCallback(async (location) => {
    // Take manual control + navigate to the location's currently authored
    // view so the curator starts from a known baseline.
    await matterportService.stopTour({ skipReturn: true });
    window.dispatchEvent(new CustomEvent('close360View'));
    try {
      setNavigating(location.id);
      await matterportService.navigateToTourSnapshotBySid(location.tourSid, location.camera || null);
      window.dispatchEvent(new CustomEvent('activeLocationChanged', {
        detail: { tourSid: location.tourSid, id: location.id }
      }));
    } catch (err) {
      console.error(`❌ Failed to navigate to ${location.id} for editing:`, err);
    } finally {
      setNavigating(null);
    }
    setEditingLocation(location);
  }, []);

  const handleCloseEditor = useCallback(() => setEditingLocation(null), []);

  // Read live pose + zoom from the SDK and emit a config-shaped block.
  // Preserves any non-camera fields the location already had (panDegrees,
  // panSpeed, flyTime) so curators can edit angles without losing tour-pan
  // settings.
  const buildCameraBlock = useCallback(() => {
    if (!editingLocation) return null;
    const pose = matterportService.getCurrentPose?.() || null;
    const sweepId = matterportService.getCurrentSweepId?.() || null;
    const zoom = matterportService.getCurrentZoom?.();
    if (!pose || !sweepId) return null;
    const prev = editingLocation.camera || {};
    const block = {
      sweepId,
      rotation: {
        x: Number(pose.rotation?.x ?? 0).toFixed(2) * 1,
        y: Number(pose.rotation?.y ?? 0).toFixed(2) * 1,
      },
    };
    if (typeof zoom === 'number') block.zoom = Number(zoom.toFixed(3)) * 1;
    // Carry forward tour-pan fields if they were set before.
    if (prev.flyTime != null) block.flyTime = prev.flyTime;
    if (prev.panDegrees != null) block.panDegrees = prev.panDegrees;
    if (prev.panSpeed != null) block.panSpeed = prev.panSpeed;
    return block;
  }, [editingLocation]);

  const handleCopyCamera = useCallback(async () => {
    const block = buildCameraBlock();
    if (!block) return;
    const text = `"camera": ${JSON.stringify(block, null, 2)}`;
    try {
      await navigator.clipboard.writeText(text);
      console.log('📋 Copied camera block:\n' + text);
    } catch (err) {
      console.warn('⚠️ Clipboard write failed, logging instead:', err?.message);
      console.log(text);
    }
  }, [buildCameraBlock]);

  // Persist the captured camera block directly into src/config/quickNavConfig.json
  // via the dev-server endpoint defined in setupProxy.js. Only available when
  // the dev server is running — falls back to clipboard with a warning if the
  // POST fails (e.g. running against a production build).
  const [saveStatus, setSaveStatus] = useState(null); // 'saving' | 'saved' | 'error'
  const handleSaveCamera = useCallback(async () => {
    const block = buildCameraBlock();
    if (!block || !editingLocation) return;
    setSaveStatus('saving');
    try {
      // Merge the new block into a fresh copy of the full config and POST
      // the whole object — the endpoint replaces the file atomically.
      const next = {
        ...quickNavConfig,
        locations: quickNavConfig.locations.map((loc) =>
          loc.id === editingLocation.id ? { ...loc, camera: block } : loc
        ),
      };
      const res = await fetch('/__dev/saveQuickNav', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(next),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.ok) throw new Error(json.error || `HTTP ${res.status}`);
      // Mutate the in-memory config so subsequent navigations use the new
      // camera without a hard reload. The next refresh will pick up the
      // file write.
      const target = quickNavConfig.locations.find((l) => l.id === editingLocation.id);
      if (target) target.camera = block;
      // Re-register the override map so future clicks honour the change.
      const overrides = {};
      for (const loc of quickNavConfig.locations) {
        if (loc.tourSid && loc.camera) overrides[loc.tourSid] = loc.camera;
      }
      matterportService.setLocationCameraOverrides?.(overrides);
      setEditingLocation((cur) => (cur ? { ...cur, camera: block } : cur));
      setSaveStatus('saved');
      console.log(`💾 Saved camera for ${editingLocation.id}`);
      setTimeout(() => setSaveStatus(null), 1800);
    } catch (err) {
      console.error('❌ Save failed:', err);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 2500);
    }
  }, [buildCameraBlock, editingLocation]);

  const handlePreviewCamera = useCallback(async () => {
    const block = buildCameraBlock();
    if (!block || !editingLocation) return;
    // Re-fly using the freshly-captured block so the curator can verify the
    // exact framing the JSON will produce.
    await matterportService.navigateToTourSnapshotBySid(editingLocation.tourSid, block);
  }, [buildCameraBlock, editingLocation]);


  if (!PROJECT_CONFIG.features.locationQuickNavEnabled || !quickNavConfig.enabled) {
    return null;
  }

  const tourText = TOUR_LABEL[language] || TOUR_LABEL.mr;
  const getLabel = (location) =>
    location.labels?.[language] || location.labels?.en || location.id;

  // Tour-only locations (excludes stops flagged `excludeFromTour`, e.g. Sky View)
  // — used for the step counter and first/last-step detection. The full
  // `locations` list is still used for the footer dock (those stops remain
  // clickable, they're just not part of the auto-played guided walk).
  const tourLocations = quickNavConfig.locations.filter((l) => !l.excludeFromTour);
  const activeIdx = activeLocationId
    ? tourLocations.findIndex(l => l.id === activeLocationId)
    : -1;
  const tourRunning = activeLocationId !== null;
  const isFirstStop = activeIdx === 0;
  const isLastStop = activeIdx === tourLocations.length - 1;
  const activeLocation = activeIdx >= 0 ? tourLocations[activeIdx] : null;
  const activeStopLabel = activeLocation ? getLabel(activeLocation) : '';

  const PAUSE_LABEL = { mr: 'विराम', en: 'Pause', hi: 'रोकें' };
  const PLAY_LABEL  = { mr: 'सुरू', en: 'Play', hi: 'चलाएँ' };
  const PREV_LABEL  = { mr: 'मागे', en: 'Prev', hi: 'पिछला' };
  const NEXT_LABEL  = { mr: 'पुढे', en: 'Next', hi: 'अगला' };
  const STOP_LABEL  = { mr: 'थांबवा', en: 'Stop', hi: 'रोकें' };

  return (
    <>
    <div className="location-quick-nav" style={{ zIndex: Z_INDEX.QUICK_NAV }}>

      {/* Left scroll arrow — inline edge button. Dims when at the start. */}
      <button
        type="button"
        className={`location-quick-nav__scroll-arrow location-quick-nav__scroll-arrow--left${canScrollLeft ? '' : ' is-hidden'}`}
        onClick={() => scrollStrip(-1)}
        aria-label="Scroll left"
        disabled={!canScrollLeft}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M15 18l-6-6 6-6" />
        </svg>
      </button>

      {/* Scrollable strip — 12 key stops (Guided-Tour controls now live in
          a separate floating panel rendered below via createPortal). */}
      <div className="location-quick-nav__locations" ref={locationsRef}>
        {quickNavConfig.locations.map((location) => {
          const isActive = activeLocationId === location.id;
          // A stop is "editable" only if it actually moves the 3D camera.
          // Sky View has no camera to author.
          const isEditable = !!location.tourSid
            && location.action !== 'skyView'
            && location.camera?.skipCamera !== true;
          const classes = [
            'location-quick-nav-btn',
            navigating === location.id ? 'navigating' : '',
            (location.thumb || location.image) ? 'has-image' : '',
            isActive ? 'active' : '',
            editMode && isEditable ? 'edit-eligible' : '',
            editingLocation?.id === location.id ? 'editing' : '',
          ].filter(Boolean).join(' ');
          return (
            <span key={location.id} className="location-quick-nav-btn-wrap">
              <button
                type="button"
                data-tour-sid={location.tourSid}
                data-location-id={location.id}
                className={classes}
                onClick={() => handleNavigate(location)}
                disabled={navigating !== null}
                title={getLabel(location)}
                aria-current={isActive ? 'true' : undefined}
              >
                {navigating === location.id
                  ? <span className="location-quick-nav-spinner" />
                  : (location.thumb || location.image)
                    ? <img
                        src={process.env.PUBLIC_URL + (location.thumb || location.image)}
                        alt=""
                        className="location-quick-nav-btn-img"
                        loading="lazy"
                        decoding="async"
                      />
                    : <span className="location-quick-nav-btn-icon">{location.icon || '📍'}</span>
                }
                <span className="location-quick-nav-btn-label">{getLabel(location)}</span>
              </button>
              {editMode && isEditable && (
                <button
                  type="button"
                  className="location-quick-nav-btn-edit"
                  title="Edit camera view"
                  aria-label={`Edit camera view for ${getLabel(location)}`}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleEditLocation(location); }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M12 20h9" />
                    <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4Z" />
                  </svg>
                </button>
              )}
            </span>
          );
        })}
      </div>

      {/* Right scroll arrow — inline edge button. Dims when at the end. */}
      <button
        type="button"
        className={`location-quick-nav__scroll-arrow location-quick-nav__scroll-arrow--right${canScrollRight ? '' : ' is-hidden'}`}
        onClick={() => scrollStrip(1)}
        aria-label="Scroll right"
        disabled={!canScrollRight}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M9 18l6-6-6-6" />
        </svg>
      </button>
    </div>

    {/* ── Floating "Now Playing" tour pill — Option A ── */}
    {/* ── Floating Guided-Tour panel ──
         When idle: shows a single "Start Tour" launcher.
         When running: morphs into Prev / Play-Pause / Next + Stop controls. */}
    {createPortal(
      tourRunning ? (
        <div
          className="tour-panel"
          style={{ zIndex: Z_INDEX.QUICK_NAV + 2 }}
          role="region"
          aria-label="Guided tour controls"
        >
          {/* Stop title row — shows current location + step counter +
              the Read-Along toggle so it reads as part of the same
              chrome rather than a separate floating pill. */}
          <div className="tour-panel__title-row">
            <span className="tour-panel__eyebrow">{tourText.label}</span>
            <h2 className="tour-panel__title" title={activeStopLabel}>
              {activeStopLabel}
            </h2>
            <span className="tour-panel__counter">
              {activeIdx + 1} <span className="tour-panel__counter-sep">/</span> {tourLocations.length}
            </span>
            {PROJECT_CONFIG.features?.readAlongCaptionsEnabled && (
              <CaptionsButton inline />
            )}
          </div>

          {/* Controls row */}
          <div className="tour-panel__controls">
            {/* Inline language switcher on the left — full native names so the
                user can change language mid-tour without leaving the panel. */}
            <div className="tour-panel__lang" role="group" aria-label="Language">
              {PROJECT_CONFIG.i18n.supportedLanguages.map((l) => (
                <button
                  key={l.code}
                  type="button"
                  className={`tour-panel__lang-btn${language === l.code ? ' is-active' : ''}`}
                  onClick={() => setLanguage(l.code)}
                  aria-pressed={language === l.code}
                  aria-label={l.label}
                  title={l.label}
                >
                  {l.label}
                </button>
              ))}
            </div>

            <span className="tour-panel__divider" aria-hidden="true" />

            <button
              type="button"
              className="tour-panel__btn"
              onClick={handleTourPrev}
              disabled={isFirstStop}
              title={PREV_LABEL[language]}
              aria-label={PREV_LABEL[language]}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 18l-6-6 6-6"/>
              </svg>
              <span className="tour-panel__btn-label">{PREV_LABEL[language]}</span>
            </button>

            <button
              type="button"
              className="tour-panel__btn tour-panel__btn--primary"
              onClick={handleTourTogglePause}
              title={tourPaused ? PLAY_LABEL[language] : PAUSE_LABEL[language]}
              aria-label={tourPaused ? PLAY_LABEL[language] : PAUSE_LABEL[language]}
            >
              {tourPaused ? (
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6 4h4v16H6zM14 4h4v16h-4z"/>
                </svg>
              )}
              <span className="tour-panel__btn-label">
                {tourPaused ? PLAY_LABEL[language] : PAUSE_LABEL[language]}
              </span>
            </button>

            <button
              type="button"
              className="tour-panel__btn"
              onClick={handleTourNext}
              disabled={isLastStop}
              title={NEXT_LABEL[language]}
              aria-label={NEXT_LABEL[language]}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 18l6-6-6-6"/>
              </svg>
              <span className="tour-panel__btn-label">{NEXT_LABEL[language]}</span>
            </button>

            <span className="tour-panel__divider" aria-hidden="true" />

            <button
              type="button"
              className="tour-panel__btn tour-panel__btn--close"
              onClick={handleTourStop}
              title={STOP_LABEL[language]}
              aria-label={STOP_LABEL[language]}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
              <span className="tour-panel__btn-label">{STOP_LABEL[language]}</span>
            </button>
          </div>
        </div>
      ) : (
        <>
        <button
          type="button"
          className={`tour-launcher${navigating === 'guided_tour' ? ' tour-launcher--loading' : ''}`}
          style={{ zIndex: Z_INDEX.QUICK_NAV + 2 }}
          onClick={handleStartTour}
          disabled={navigating !== null}
          title={tourText.label}
          aria-label={tourText.label}
        >
          {navigating === 'guided_tour' ? (
            <span className="tour-launcher__spinner" />
          ) : (
            <svg className="tour-launcher__icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M8 5v14l11-7z"/>
            </svg>
          )}
          <span className="tour-launcher__label">{tourText.label}</span>
        </button>
        {/* Curator: visible only after Alt+Shift+E enables edit mode.
            Stacked just above the guided-tour launcher so it shares the
            bottom-left chrome column. Click it to exit edit mode. */}
        {editMode && (
          <button
            type="button"
            className="tour-launcher quicknav-edit-toggle is-active"
            style={{ zIndex: Z_INDEX.QUICK_NAV + 2 }}
            onClick={() => { setEditMode(false); setEditingLocation(null); }}
            title="Exit edit mode (Alt+Shift+E)"
            aria-label="Exit edit mode"
            aria-pressed="true"
          >
            <svg className="tour-launcher__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 20h9" />
              <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4Z" />
            </svg>
            <span className="tour-launcher__label">Exit Edit</span>
          </button>
        )}
        </>
      ),
      document.body
    )}

    {/* ── Camera-view editor panel (Alt+Shift+E curator tool) ──
         Visible only when the operator picks a 3D footer stop while in
         edit mode. Reads live pose from the SDK and exports a config-shaped
         JSON block they can paste into quickNavConfig.json. */}
    {editingLocation && createPortal(
      (() => {
        const pose = matterportService.getCurrentPose?.() || null;
        const sweepId = matterportService.getCurrentSweepId?.() || '—';
        const zoom = matterportService.getCurrentZoom?.();
        const pitch = pose?.rotation?.x;
        const yaw = pose?.rotation?.y;
        return (
          <div
            className="quicknav-edit-panel"
            style={{ zIndex: Z_INDEX.QUICK_NAV + 3 }}
            role="dialog"
            aria-label="Edit camera view"
          >
            <div className="quicknav-edit-panel__header">
              <span className="quicknav-edit-panel__eyebrow">Edit View</span>
              <h3 className="quicknav-edit-panel__title">{getLabel(editingLocation)}</h3>
              <button
                type="button"
                className="quicknav-edit-panel__close"
                onClick={handleCloseEditor}
                aria-label="Close editor"
                title="Close (Alt+Shift+E to exit edit mode)"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p className="quicknav-edit-panel__hint">
              Pan, look around, and zoom in the 3D scene. The values below
              update live. Click <strong>Copy JSON</strong> to capture the
              current view as the new <code>camera</code> block for this stop.
            </p>
            <dl className="quicknav-edit-panel__readout">
              <dt>sweepId</dt>
              <dd><code>{sweepId}</code></dd>
              <dt>pitch (x)</dt>
              <dd>{pitch != null ? pitch.toFixed(2) + '°' : '—'}</dd>
              <dt>yaw (y)</dt>
              <dd>{yaw != null ? yaw.toFixed(2) + '°' : '—'}</dd>
              <dt>zoom</dt>
              <dd>{typeof zoom === 'number' ? zoom.toFixed(3) : '—'}</dd>
            </dl>
            <div className="quicknav-edit-panel__actions">
              <button
                type="button"
                className="quicknav-edit-panel__btn"
                onClick={handlePreviewCamera}
                title="Re-apply the captured view to verify framing"
              >
                Preview
              </button>
              <button
                type="button"
                className="quicknav-edit-panel__btn"
                onClick={handleCopyCamera}
                title="Copy a JSON camera block to the clipboard"
              >
                Copy JSON
              </button>
              <button
                type="button"
                className={`quicknav-edit-panel__btn quicknav-edit-panel__btn--primary${saveStatus === 'error' ? ' quicknav-edit-panel__btn--error' : ''}`}
                onClick={handleSaveCamera}
                disabled={saveStatus === 'saving'}
                title="Write the captured view directly into quickNavConfig.json"
              >
                {saveStatus === 'saving' ? 'Saving…'
                  : saveStatus === 'saved' ? 'Saved ✓'
                  : saveStatus === 'error' ? 'Failed'
                  : 'Save'}
              </button>
            </div>
          </div>
        );
      })(),
      document.body
    )}
    </>
  );
};

export default LocationQuickNav;
